/**
 * Cart page only: if no membership products remain, remove all reward pack products.
 * Force full page load, but check into ajax options
 */

const DEBUG = false;

// Membership product ids
// TODO: Move to tag/collection
const MEMBERSHIP_PRODUCT_IDS = [
  8805806506115,  // Silver
  8805811323011,  // Gold
  8805812633731,  // Platinum
];

const REWARD_PACK_PRODUCT_IDS = [
  8816462332035, // Reward Pack 1
  8816462299267, // Reward Pack 2 
  8816462266499, // Reward Pack 3
];

const membershipSet = new Set(MEMBERSHIP_PRODUCT_IDS.map(Number));
const rewardSet = new Set(REWARD_PACK_PRODUCT_IDS.map(Number));

function customerHasActiveMembership() {
  return Array.isArray(window.CUSTOMER_TAGS) &&
         window.CUSTOMER_TAGS.includes("active-membership");
}


let inFlight = false;
let queued = false;

// Prevent the fetch hook from re-triggering enforcement due to our own cleanup calls
let INTERNAL_CART_MUTATION = false;

// Debounce scheduling so multiple cart calls in a burst only run one enforcement
let enforceTimer = null;
function scheduleEnforce(delayMs = 0) {
  if (enforceTimer) clearTimeout(enforceTimer);
  enforceTimer = setTimeout(() => {
    enforceTimer = null;
    enforceDependency();
  }, delayMs);
}

function log(...args) {
  if (DEBUG) console.log("[cart-dependency]", ...args);
}

async function fetchCart() {
  const res = await fetch("/cart.js", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`cart.js failed: ${res.status}`);
  return res.json();
}

async function removeRewardPacksInOneCall(cart) {
  // Build updates map: { variant_id: 0 } for reward packs currently in cart
  const updates = {};
  const toRemove = [];

  for (const item of cart.items) {
    const pid = Number(item.product_id);
    const vid = Number(item.variant_id);

    if (rewardSet.has(pid)) {
      updates[vid] = 0;
      toRemove.push({
        title: item.title,
        product_id: pid,
        variant_id: vid,
        key: item.key,
      });
    }
  }

  log("Reward packs detected for removal:", toRemove);

  if (!Object.keys(updates).length) return { changed: false };

  INTERNAL_CART_MUTATION = true;
  try {
    const res = await fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates }),
    });

    if (!res.ok) throw new Error(`cart/update.js failed: ${res.status}`);
  } finally {
    INTERNAL_CART_MUTATION = false;
  }

  return { changed: true };
}

/**
 * Core rule:
 * If there are no membership products in cart, remove all reward pack products in cart.
 */
async function enforceDependency() {
  // NEW RULE: if customer already has an active membership, allow reward packs to remain
  if (customerHasActiveMembership()) {
    log("Customer has active-membership tag; skipping reward pack enforcement.");
    return;
  }

  if (inFlight) {
    queued = true;
    return;
  }
  inFlight = true;

  try {
    const cart = await fetchCart();

    // Debug snapshot
    log("Cart items snapshot:");
    cart.items.forEach((i) => {
      log(`- ${i.title}`, {
        product_id: i.product_id,
        variant_id: i.variant_id,
        key: i.key,
        has_selling_plan_allocation: !!i.selling_plan_allocation,
      });
    });

    const hasMembership = cart.items.some((i) => membershipSet.has(Number(i.product_id)));

    log("Membership present?", hasMembership);
    log("Cart attributes:", cart.attributes);

    if (hasMembership) return;

    const result = await removeRewardPacksInOneCall(cart);
    log("Removal performed?", result.changed);

    // Force full page load.
    // TODO: Option to use existing cart update logic?
    if (result.changed) {
      window.location.reload();
    }
  } catch (e) {
    console.warn("[cart-dependency] enforcement failed", e);
  } finally {
    inFlight = false;
    if (queued) {
      queued = false;
      // Run once more in case the cart changed while we were in flight
      scheduleEnforce(0);
    }
  }
}

/**
 * Hook fetch so we enforce after the theme finishes cart mutations.
 * This avoids the "click fires before /cart/change completes" timing issue. (in theory)
 */
(function hookCartFetch() {
  const origFetch = window.fetch;

  window.fetch = async function (resource, init) {
    const res = await origFetch.apply(this, arguments);

    try {
      const url = typeof resource === "string" ? resource : (resource?.url || "");

      const isCartMutation =
        url.includes("/cart/change") ||
        url.includes("/cart/update") ||
        url.includes("/cart/add") ||
        url.includes("/cart/clear");

      // Only enforce after external/theme mutations, not our own cleanup calls
      if (isCartMutation && !INTERNAL_CART_MUTATION) {
        log("Detected cart mutation completed:", url);
        // slight debounce: Horizon may do multiple cart calls in quick succession
        scheduleEnforce(50);
      }
    } catch (_) {}

    return res;
  };
})();

// Run on load (covers page refresh / direct cart visits)
document.addEventListener("DOMContentLoaded", () => {
  log(
    "Loaded cart dependency script. Membership products:",
    [...membershipSet],
    "Reward pack products:",
    [...rewardSet]
  );

  // Initial enforcement (in case cart already violates dependency)
  scheduleEnforce(0);
});